/**
 * End User View
 * Beautiful, production-ready interface for operators
 */

import { useState, useEffect } from 'react'
import { useMScaleStore } from '../store'
import { useBluetooth } from '../hooks/useBluetooth'
import { ScaleView } from '../components/Scale/ScaleView'
import { Button } from '../components/Common/Button'
import { formatWeight } from '../utils/format'

const GROUP_COLORS = {
  0: { color: '#00FF00', name: 'Green' },
  1: { color: '#0064FF', name: 'Blue' },
  2: { color: '#FFDC00', name: 'Yellow' },
  3: { color: '#FF0000', name: 'Red' },
}

export function EndUserView() {
  const { groups, targetRange, deviceName, isDualDeviceMode, deviceLinkStatus, showHundredthsUnder10g } = useMScaleStore()
  const { tareAll, tarePosition } = useBluetooth()
  const [rotation, setRotation] = useState(0)
  const [isRotationLoaded, setIsRotationLoaded] = useState(false)

  const activeGroups = groups?.filter(g => g.isValid) || []
  const scaleCount = isDualDeviceMode ? 32 : 16

  // Load rotation from localStorage when device connects
  useEffect(() => {
    if (deviceName) {
      const storageKey = `m-scale-rotation-${deviceName}`
      const savedRotation = localStorage.getItem(storageKey)
      console.log(`Loading rotation for ${deviceName}:`, savedRotation)
      if (savedRotation) {
        const rotationValue = parseInt(savedRotation, 10)
        if ([0, 90, 180, 270].includes(rotationValue)) {
          setRotation(rotationValue)
        }
      }
      setIsRotationLoaded(true)
    } else {
      // Reset when device disconnects
      setIsRotationLoaded(false)
      setRotation(0)
    }
  }, [deviceName])

  // Save rotation to localStorage when it changes (but only after initial load)
  useEffect(() => {
    if (deviceName && isRotationLoaded) {
      const storageKey = `m-scale-rotation-${deviceName}`
      console.log(`Saving rotation for ${deviceName}:`, rotation)
      localStorage.setItem(storageKey, rotation.toString())
    }
  }, [rotation, deviceName, isRotationLoaded])

  const handleTareAll = async () => {
    try {
      await tareAll()
    } catch (error) {
      console.error('Failed to tare scales:', error)
    }
  }

  const handleTarePosition = async (position: number) => {
    try {
      await tarePosition(position)
    } catch (error) {
      console.error('Failed to tare position:', error)
    }
  }

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360)
  }

  return (
    <div className="min-h-screen lg:h-full flex flex-col lg:flex-row gap-3 sm:gap-4 p-3 sm:p-4 lg:overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 dark:from-industrial-bg dark:to-gray-900">
      {/* Scale Visualization - Takes majority of space */}
      <div className="flex-1 min-h-[300px] lg:min-h-0 flex items-center justify-center lg:overflow-hidden">
        <div className="w-full h-full max-w-2xl max-h-full mx-auto">
          <ScaleView onPadClick={handleTarePosition} rotation={rotation} />
        </div>
      </div>

      {/* Side Panel - Group Information */}
      <aside className="lg:w-80 flex-shrink-0 flex flex-col gap-3 lg:overflow-y-auto">
        {/* Dual-Device Mode Indicator */}
        {isDualDeviceMode && deviceLinkStatus && (
          <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-3 py-2 rounded-lg shadow-lg">
            <div className="flex items-center gap-2">
              <div className="text-lg">🔗</div>
              <div className="flex-1">
                <div className="text-xs font-semibold">Dual-Device Mode</div>
                <div className="text-xs opacity-90">{scaleCount} scales active</div>
              </div>
            </div>
          </div>
        )}

        {/* Control Buttons */}
        <div className="flex gap-2">
          <Button
            variant="primary"
            onClick={handleTareAll}
            className="flex-1 text-sm sm:text-base"
          >
            Tare All Scales
          </Button>
          {/* Rotation only available in single-device mode */}
          {!isDualDeviceMode && (
            <Button
              variant="secondary"
              onClick={handleRotate}
              className="px-3 sm:px-4 text-lg sm:text-xl"
              title="Rotate display 90°"
            >
              ↻
            </Button>
          )}
        </div>

        {/* Active Groups */}
        <div className="bg-white/90 dark:bg-industrial-surface/90 backdrop-blur-sm rounded-xl shadow-lg p-3 sm:p-4 border border-gray-200 dark:border-industrial-border">
            <h2 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-3">
              Active Groups
            </h2>

            {activeGroups.length === 0 ? (
              <div className="text-center py-4 sm:py-6">
                <div className="text-2xl sm:text-3xl mb-2">⚖️</div>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-industrial-text-dim">
                  No groups detected
                </p>
                <p className="text-xs text-gray-400 dark:text-industrial-text-dim mt-1">
                  Place items within target range
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {activeGroups.map((group, index) => {
                  const groupColor = GROUP_COLORS[index as keyof typeof GROUP_COLORS]
                  return (
                    <div
                      key={index}
                      className="p-2.5 sm:p-3 rounded-lg border-2 transition-all duration-300"
                      style={{
                        borderColor: groupColor.color,
                        backgroundColor: `${groupColor.color}10`,
                        boxShadow: `0 0 20px ${groupColor.color}40`,
                      }}
                    >
                      <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                        <span
                          className="text-xs sm:text-sm font-semibold"
                          style={{ color: groupColor.color }}
                        >
                          {groupColor.name}
                        </span>
                        <div className="text-[10px] sm:text-xs text-gray-500 dark:text-industrial-text-dim">
                          Pos {group.positions.join(' + ')}
                        </div>
                      </div>
                      <div className="text-xl sm:text-2xl font-mono font-bold text-gray-900 dark:text-white">
                        {formatWeight(group.totalWeight, showHundredthsUnder10g)}g
                      </div>
                      <div className="flex gap-2 text-[10px] sm:text-xs text-gray-600 dark:text-industrial-text-dim mt-1">
                        {group.weights.map((w, i) => (
                          <span key={i}>{formatWeight(w, showHundredthsUnder10g)}g{i < group.weights.length - 1 ? ' +' : ''}</span>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

        {/* Target Range Info */}
        <div className="bg-white/90 dark:bg-industrial-surface/90 backdrop-blur-sm rounded-xl shadow-lg p-3 sm:p-4 border border-gray-200 dark:border-industrial-border">
          <h3 className="text-xs font-semibold text-gray-700 dark:text-industrial-text-dim mb-2">
            Target Range
          </h3>
          <div className="flex items-baseline gap-1.5 sm:gap-2 flex-wrap">
            <span className="text-xl sm:text-2xl font-mono font-bold text-gray-900 dark:text-white">
              {targetRange.min.toFixed(1)}
            </span>
            <span className="text-gray-500 dark:text-industrial-text-dim">—</span>
            <span className="text-xl sm:text-2xl font-mono font-bold text-gray-900 dark:text-white">
              {targetRange.max.toFixed(1)}
            </span>
            <span className="text-xs text-gray-500 dark:text-industrial-text-dim">grams</span>
          </div>
        </div>
      </aside>
    </div>
  )
}
